import React, { useState, useCallback, useEffect } from 'react';
import HeaderWithIcon from '../../components/common/HeaderWithIcon';
import ManageGamesTabs from './ManageGameTabs';
import ManageHeader from './ManageHeader';
import toast, { Toaster } from 'react-hot-toast';
import getGameType from '../../utils/games';
import { useAuth } from '../../context/AuthContext';
import { gamesApi } from '../../api/games';
import ModifyGame from '../../components/Popup/ModifyGame';

export default function ManageGames() {
  const { tokens, user } = useAuth();
  const [activeTab, setActiveTab] = useState('All');
  const [activeFilter, setActiveFilter] = useState('active');
  const [ownedGames, setOwnedGames] = useState([]);
  const [allGames, setAllGames] = useState([]);
  const [allGameSettings, setAllGameSettings] = useState([]);
  const [gameSettingsMap, setGameSettingsMap] = useState({});
  const [modifyGamePopupOpen, setModifyGamePopupOpen] = useState(false);
  const [selectedGameToModify, setSelectedGameToModify] = useState(null);
  const isAdmin = user && user.roleId === 1;

  const fetchGamesData = useCallback(async () => {
    try {
      const allGamesRes = await gamesApi.getAllGames(tokens.access.token);
      if (allGamesRes.status === 200) {
        const imageMap = allGamesRes.data.reduce(
          (acc, game) => ({
            ...acc,
            [game.id]: {
              image: getGameType(game.otherSettings.image),
              name: game.name,
            },
          }),
          {}
        );
        setAllGames(imageMap);
        setAllGameSettings(allGamesRes.data);

        // Create a map of game settings for easier lookup
        const gameSettingsMapData = {};
        allGamesRes.data.forEach((game) => {
          gameSettingsMapData[game.id] = game.name;
        });
        setGameSettingsMap(gameSettingsMapData);

        const ownedRes = await gamesApi.ownedGames(tokens.access.token);
        if (ownedRes.status === 200) {
          const updatedOwned = ownedRes.data.map((game) => ({
            ...game,
            image: imageMap[game.game_settings_id],
          }));
          setOwnedGames(updatedOwned.sort((a, b) => a.id - b.id));
        }
      }
    } catch (error) {
      toast.error(`Error fetching games: ${error.message}`);
    }
  }, [tokens.access.token]);

  useEffect(() => {
    fetchGamesData();
  }, [fetchGamesData]);

  const handleToggle = async (id, active) => {
    try {
      const gameToUpdate = ownedGames.find((game) => game.id === id);
      if (!gameToUpdate) return;

      const response = await gamesApi.updateGame(tokens.access.token, id, {
        active: !active,
        name: gameToUpdate.name,
        commissionRate: parseInt(gameToUpdate.commissionRate),
        winRate: parseInt(gameToUpdate.winRate),
        doubleWin: parseInt(gameToUpdate.doubleWin),
        tripleWin: parseInt(gameToUpdate.tripleWin),
        gameSettingsId: gameToUpdate.game_settings_id,
        userId: gameToUpdate.user_id,
      });

      if (response.status === 200) {
        toast.success(
          `Game ${!active ? 'activated' : 'deactivated'} successfully`
        );
        fetchGamesData(); // Refresh games after update
      } else {
        toast.error('Failed to update game status');
      }
    } catch (error) {
      toast.error('Error updating game status');
      console.error('Error updating game status:', error);
    }
  };

  const handleModify = (game) => {
    setSelectedGameToModify(game);
    setModifyGamePopupOpen(true);
  };

  const handleDelete = (id) => {
    console.log(`Delete game ${id}`);
  };

  // Group games by game_settings_id
  const getGroupedGamesBySettings = () => {
    const groupedGames = {};

    // Create "All" group first
    groupedGames['All'] = {
      title: 'All',
      cards: ownedGames,
    };

    // Group by game_settings_id
    ownedGames.forEach((game) => {
      const settingsId = game.game_settings_id;

      // Find the correct game settings name
      let settingsName = `Game ${settingsId}`;
      const matchingGameSetting = allGameSettings.find(
        (setting) => setting.id === settingsId
      );
      if (matchingGameSetting) {
        settingsName = matchingGameSetting.name;
      }

      if (!groupedGames[settingsId]) {
        groupedGames[settingsId] = {
          title: settingsName,
          cards: [],
        };
      }

      groupedGames[settingsId].cards.push(game);
    });

    return Object.values(groupedGames);
  };

  const tabsData = getGroupedGamesBySettings();

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleSaveModifiedGame = async (gameData) => {
    if (!selectedGameToModify) return;
    try {
      const response = await gamesApi.updateGame(tokens.access.token, selectedGameToModify.id, {
        name: gameData.name,
        commissionRate: parseInt(gameData.commissionRate),
        winRate: parseInt(gameData.winRate),
        doubleWin: parseInt(gameData.doubleWin),
        tripleWin: parseInt(gameData.tripleWin),
        gameSettingsId: gameData.gameSettingsId,
        userId: selectedGameToModify.user_id,
        isPublic: gameData.isPublic,
      });
      if (response.status === 200) {
        toast.success('Game updated successfully');
        setModifyGamePopupOpen(false);
        fetchGamesData();
      } else {
        toast.error('Failed to update game');
      }
    } catch (error) {
      toast.error('Error updating game');
      console.error('Error updating game:', error);
    }
  };

  return (
    <div className='min-h-screen relative overflow-hidden font-poppins'>
      <HeaderWithIcon title='Manage Games' />

      {isAdmin && (
        <ManageHeader
          allGameSettings={allGameSettings}
          refreshGames={fetchGamesData}
        />
      )}

      {/* Replace the direct listing with tabs */}
      <ManageGamesTabs
        tabsData={tabsData}
        allGameSettings={allGameSettings}
        refreshGames={fetchGamesData}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onToggle={handleToggle}
        onModify={handleModify}
      />

      <Toaster
        toastOptions={{
          position: 'bottom-center',
          duration: 1000,
        }}
      />

      {modifyGamePopupOpen && selectedGameToModify && (
        <ModifyGame
          isOpen={modifyGamePopupOpen}
          onClose={() => setModifyGamePopupOpen(false)}
          onSave={handleSaveModifiedGame}
          gameData={selectedGameToModify}
          allGameSettings={allGameSettings}
        />
      )}
    </div>
  );
}
